import { useCallback, useEffect, useState } from "react";
import {
  FiTrendingUp,
  FiShoppingCart,
  FiFileText,
  FiRotateCcw,
  FiDollarSign,
  FiUsers,
  FiAward,
  FiCreditCard,
  FiRefreshCw,
  FiAlertCircle,
} from "react-icons/fi";

import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

import StatCard from "./components/StatCard";
import SalesOverview from "./components/SaleOverview";
import RecentSalesOrders from "./components/RecentSalesOrders";
import RecentInvoices from "./components/RecentInvoices";
import TopSellingProducts from "./components/TopSellingProducts";
import TopCustomers from "./components/TopCustomers";
import SalesActivityTimeline from "./components/SalesActivityTimeline";

export default function SalesDashboard() {
  const { accessToken } = useAuth();

  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/sales-dashboard", {
        accessToken,
      });

      setDashboard(response);
    } catch (requestError) {
      setError(requestError.message || "Could not load sales dashboard.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (isLoading) {
    return (
      <div className="min-h-[440px] flex items-center justify-center text-primary-700">
        <FiRefreshCw className="animate-spin mr-3" size={24} />
        Loading sales dashboard...
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="bg-white rounded-3xl border border-red-100 p-8">
        <div className="flex gap-3 text-red-700">
          <FiAlertCircle className="mt-0.5" />
          <p>{error || "Sales dashboard could not be loaded."}</p>
        </div>

        <button
          onClick={loadDashboard}
          className="mt-5 h-11 px-5 rounded-xl bg-primary-700 text-white"
        >
          Try Again
        </button>
      </div>
    );
  }

  const { kpis, snapshot } = dashboard;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-end">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Dashboard</h1>

          <p className="text-gray-500 mt-2">
            Live summary of ERP invoices, POS sales, online orders, and customer
            collections.
          </p>
        </div>

        <button
          onClick={loadDashboard}
          className="h-11 px-5 rounded-xl border border-gray-200 bg-white flex items-center gap-2 hover:bg-gray-50"
        >
          <FiRefreshCw />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-yellow-200 bg-yellow-50 px-5 py-4 text-yellow-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title="Today's Sales"
          value={formatMoney(kpis.todaySales)}
          subtitle={formatChange(kpis.salesChangePercent)}
          bgColor="text-green-600"
          iconBg="bg-green-100"
          icon={<FiTrendingUp size={28} className="text-green-600" />}
        />

        <StatCard
          title="Orders Today"
          value={kpis.ordersToday}
          subtitle={`${snapshot.readyToDispatch} ready to dispatch`}
          bgColor="text-blue-600"
          iconBg="bg-blue-100"
          icon={<FiShoppingCart size={28} className="text-blue-600" />}
        />

        <StatCard
          title="Invoices Issued"
          value={kpis.invoicesToday}
          subtitle="Generated today"
          bgColor="text-purple-600"
          iconBg="bg-purple-100"
          icon={<FiFileText size={28} className="text-purple-600" />}
        />

        <StatCard
          title="POS Returns"
          value={kpis.salesReturnsToday}
          subtitle={`${formatMoney(kpis.salesReturnsValue)} refunded today`}
          bgColor="text-red-600"
          iconBg="bg-red-100"
          icon={<FiRotateCcw size={28} className="text-red-600" />}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title="Outstanding"
          value={formatMoney(kpis.outstandingAmount)}
          subtitle="Customer receivables"
          bgColor="text-orange-600"
          iconBg="bg-orange-100"
          icon={<FiDollarSign size={28} className="text-orange-600" />}
        />

        <StatCard
          title="Customers Today"
          value={kpis.customersToday}
          subtitle={`${kpis.newCustomersToday} new customers`}
          bgColor="text-cyan-600"
          iconBg="bg-cyan-100"
          icon={<FiUsers size={28} className="text-cyan-600" />}
        />

        <StatCard
          title="Average Order"
          value={formatMoney(kpis.averageOrderValue)}
          subtitle="Across today’s recorded sales"
          bgColor="text-indigo-600"
          iconBg="bg-indigo-100"
          icon={<FiCreditCard size={28} className="text-indigo-600" />}
        />

        <StatCard
          title="Top Brand"
          value={kpis.topBrand?.name || "—"}
          subtitle={
            kpis.topBrand
              ? `${formatMoney(kpis.topBrand.revenue)} sold today`
              : "No sales today"
          }
          bgColor="text-yellow-600"
          iconBg="bg-yellow-100"
          icon={<FiAward size={28} className="text-yellow-600" />}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <SalesOverview
            dailyTrend={dashboard.dailyTrend}
            onlineSalesVisible={dashboard.snapshot.onlineSalesVisible}
          />
        </div>

        <div className="bg-white rounded-3xl border border-gray-100 p-6">
          <h2 className="text-lg font-semibold">Sales Snapshot</h2>

          <div className="space-y-5 mt-6">
            <Snapshot label="Pending Orders" value={snapshot.pendingOrders} />
            <Snapshot
              label="Ready To Dispatch"
              value={snapshot.readyToDispatch}
            />
            <Snapshot
              label="Dispatched Today"
              value={snapshot.dispatchedToday}
            />
            <Snapshot
              label="Cancelled Orders"
              value={snapshot.cancelledOrdersToday}
            />
            <Snapshot
              label="POS Refunds"
              value={formatMoney(snapshot.refundsToday)}
            />
            <Snapshot
              label="POS Sales"
              value={formatMoney(snapshot.posSalesToday)}
            />

            {snapshot.onlineSalesVisible && (
              <Snapshot
                label="Online Sales"
                value={formatMoney(snapshot.onlineSalesToday)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <RecentSalesOrders orders={dashboard.recentSalesOrders} />
        <RecentInvoices invoices={dashboard.recentInvoices} />
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <TopSellingProducts products={dashboard.topProducts} />
        <TopCustomers customers={dashboard.topCustomers} />
      </div>

      <SalesActivityTimeline
        invoices={dashboard.recentInvoices}
        payments={dashboard.recentPayments}
        orders={dashboard.recentSalesOrders}
      />
    </div>
  );
}

function Snapshot({ label, value }) {
  return (
    <div className="flex justify-between items-center border-b border-gray-100 pb-3">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatChange(value) {
  if (value === null || value === undefined) {
    return "No prior-day comparison";
  }

  const prefix = value >= 0 ? "+" : "";
  return `${prefix}${Number(value).toFixed(1)}% vs yesterday`;
}
